import { useEffect, useRef, useCallback } from 'react';
import { trackingService } from '../services/trackingService';

export const useTracking = (formName = null) => {
  const formStartTime = useRef(null);
  const sessionId = useRef(trackingService?.sessionId);

  useEffect(() => {
    if (formName) {
      formStartTime.current = Date.now();
      trackingService?.logFormEvent({
        formName,
        eventType: 'form_start',
        timeSpentSeconds: 0
      });
    }
  }, [formName]);

  const logError = useCallback((error, metadata = {}) => {
    trackingService?.logError({
      errorType: error?.name || 'Error',
      errorMessage: error?.message || 'Unknown error',
      errorStack: error?.stack || null,
      pageUrl: window?.location?.href,
      metadata
    });
  }, []);

  const logActivity = useCallback((activityType, activityDescription, metadata = {}) => {
    trackingService?.logActivity({
      activityType,
      activityDescription,
      pageUrl: window?.location?.href,
      metadata
    });
  }, []);

  const logFormFieldChange = useCallback((fieldName, fieldValue = null) => {
    if (!formName) return;
    
    const timeSpent = formStartTime?.current 
      ? Math.floor((Date.now() - formStartTime?.current) / 1000)
      : 0;

    trackingService?.logFormEvent({
      formName,
      eventType: 'field_change',
      fieldName,
      fieldValue: fieldValue ? String(fieldValue) : null,
      timeSpentSeconds: timeSpent
    });
  }, [formName]);

  const logFormFieldBlur = useCallback((fieldName) => {
    if (!formName) return;
    
    const timeSpent = formStartTime?.current 
      ? Math.floor((Date.now() - formStartTime?.current) / 1000)
      : 0;

    trackingService?.logFormEvent({
      formName,
      eventType: 'field_blur',
      fieldName,
      timeSpentSeconds: timeSpent
    });
  }, [formName]);

  const logValidationError = useCallback((fieldName, errorMessage) => {
    if (!formName) return;
    
    const timeSpent = formStartTime?.current 
      ? Math.floor((Date.now() - formStartTime?.current) / 1000)
      : 0;

    trackingService?.logFormEvent({
      formName,
      eventType: 'validation_error',
      fieldName,
      errorMessage,
      timeSpentSeconds: timeSpent
    });
  }, [formName]);

  const logFormSubmit = useCallback((success = true, metadata = {}) => {
    if (!formName) return;
    
    const timeSpent = formStartTime?.current 
      ? Math.floor((Date.now() - formStartTime?.current) / 1000)
      : 0;

    trackingService?.logFormEvent({
      formName,
      eventType: 'form_submit',
      timeSpentSeconds: timeSpent,
      metadata: { success, ...metadata }
    });
  }, [formName]);

  const logFormAbandon = useCallback((lastFieldName = null) => {
    if (!formName) return;
    
    const timeSpent = formStartTime?.current 
      ? Math.floor((Date.now() - formStartTime?.current) / 1000)
      : 0;

    trackingService?.logFormEvent({
      formName,
      eventType: 'form_abandon',
      fieldName: lastFieldName,
      timeSpentSeconds: timeSpent
    });
  }, [formName]);

  const logPerformance = useCallback((metricType, metricName, durationMs, success = true, errorMessage = null) => {
    trackingService?.logPerformance({
      metricType,
      metricName,
      durationMs,
      pageUrl: window?.location?.href,
      success,
      errorMessage
    });
  }, []);

  useEffect(() => {
    if (!formName) return;

    const handleBeforeUnload = () => {
      const timeSpent = formStartTime?.current 
        ? Math.floor((Date.now() - formStartTime?.current) / 1000)
        : 0;

      if (timeSpent > 5) {
        navigator?.sendBeacon(
          `${import.meta.env?.VITE_SUPABASE_URL}/rest/v1/form_analytics`,
          JSON.stringify({
            session_id: sessionId?.current,
            form_name: formName,
            event_type: 'form_abandon',
            time_spent_seconds: timeSpent
          })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formName]);

  return {
    logError,
    logActivity,
    logFormFieldChange,
    logFormFieldBlur,
    logValidationError,
    logFormSubmit,
    logFormAbandon,
    logPerformance
  };
};